import { installationQuotationStatusList } from "@/features/projects/filter/InstallationQuotationStatusFilter";
import { enabledNba } from "@/features/projects/filter/NbaFilter";
import {
  type WidgetSettingsDefinition,
  isOpportunityStatus,
  workOrderStatusList,
  workOrderTypeList,
} from "@repo/api";
import { t } from "@repo/translation";
import {
  translateClassification,
  translateConsentFilterStatus,
  translateCustomerType,
  translateInstallationQuotationStatus,
  translateInstallationType,
  translateLanguage,
  translateNbaType,
  translateOpportunityType,
  translateOrderMethod,
  translateOrderStatus,
  translateProjectStatus,
  translateServices,
  translateStage,
} from "@repo/ui";
import { hasItems, isDefined } from "@repo/utils";

import { COMMON_SETTINGS } from "./commonWidgetSettings";
import { type WidgetSettingsProps } from "./widgetsSettings";

export const PROJECTS_SETTINGS = ({
  hasTags,
  supportedLanguages,
  bokaServices,
  orderMethods,
  isPromotionalConsentEnabled,
  isColdProjectEnabled,
  isKICEnabled,
  isSalesForceEnabled,
  isProjectStagesEnabled,
  projectStageValues,
}: WidgetSettingsProps): WidgetSettingsDefinition[] =>
  [
    COMMON_SETTINGS.COWORKER,
    COMMON_SETTINGS.STORE,
    {
      id: "CREATED_DATE",
      title: "label.created",
      type: "date_range",
    },
    {
      id: "UPDATED_DATE",
      title: "label.updated",
      type: "date_range",
    },
    {
      id: "TYPE",
      title: "label.type",
      type: "selection",
      translator: (key: string) => {
        return translateOpportunityType(key as any);
      },
      stringValues: [
        "Blocked Order",
        "Sales Opportunity",
        "Boka",
        "ENR",
        "Quotation",
        "Assistance Request",
      ],
      additionalControl: { showSearch: false },
    },
    {
      id: "DEPARTMENT",
      title: "label.department",
      type: "department_select",
    },
    {
      id: "STATUS",
      title: "label.status",
      type: "selection",
      translator: (key: string) => {
        const k = isOpportunityStatus(key) ? key?.replaceAll("_", "") : key;
        return translateProjectStatus(k as any);
      },
      stringValues: [
        "UNASSIGNED",
        "ASSIGNED",
        "IN_PROGRESS",
        "CLOSED_WON",
        "CLOSED_LOST",
        "INVALID",
        "NO_ACTION_PLANNED",
        isColdProjectEnabled ? "COLD_PROJECT" : undefined,
      ].filter(isDefined),
      additionalControl: { showSearch: false },
    },
    isProjectStagesEnabled
      ? {
          id: "STAGE",
          title: "label.stage",
          type: "selection",
          translator: (key: string) => translateStage(key),
          stringValues: projectStageValues ?? [],
          additionalControl: { showSearch: false },
        }
      : undefined,
    {
      id: "PROJECT_VALUE",
      title: "label.value",
      type: "value_range",
    },
    {
      id: "CUSTOMER_TYPE",
      title: "label.customer_type",
      type: "selection",
      translator: (key: string) => translateCustomerType(key),
      stringValues: ["Individual", "Business"],
      additionalControl: { showSearch: false },
    },
    hasItems(supportedLanguages)
      ? {
          id: "PREFERRED_LANGUAGE",
          title: "label.preferred_language",
          type: "selection",
          translator: (key: string) => translateLanguage(key),
          stringValues: supportedLanguages ?? [],
          additionalControl: { showSearch: false },
        }
      : undefined,
    {
      id: "CLASSIFICATION",
      title: "label.classification",
      type: "selection",
      translator: (key: string) => {
        return translateClassification(key as any);
      },
      stringValues: ["Urgent", "High", "Medium", "Low"],
      additionalControl: { showSearch: false },
    },
    {
      id: "SERVICES",
      title: "label.interested_services_filter",
      type: "interested_services",
    },
    {
      id: "FOLLOW_UP_CONSENT",
      title: "consent.FOLLOWUP_CONSENT",
      type: "selection",
      translator: (key: string) => {
        return translateConsentFilterStatus(key.toLowerCase()) ?? key;
      },
      stringValues: ["YES", "NO", "PENDING"],
      additionalControl: { showSearch: false, selectionMode: "SINGLE" },
    },
    isPromotionalConsentEnabled
      ? {
          id: "PROMOTIONAL_CONSENT",
          title: "consent.PROMOTIONAL_CONSENT",
          type: "selection",
          translator: (key: string) => {
            return translateConsentFilterStatus(key.toLowerCase()) ?? key;
          },
          stringValues: ["YES", "NO", "PENDING"],
          additionalControl: { showSearch: false, selectionMode: "SINGLE" },
        }
      : undefined,
    hasTags
      ? {
          id: "TAGS",
          title: "label.tags",
          type: "tag_select",
        }
      : undefined,
    {
      id: "ORDER_METHOD",
      title: "label.order_method",
      type: "selection" as const,
      translator: (key: string) => translateOrderMethod(key),
      stringValues: orderMethods ?? [],
    },
    {
      id: "ORDER_STATUS",
      title: "label.order_status",
      type: "selection",
      translator: (key: string) => translateOrderStatus(key),
      stringValues: ["unblocked", "created", "completed", "cancelled"],
      additionalControl: { showSearch: false },
    },
    {
      id: "NEXT_BEST_ACTIONS",
      title: "label.next_best_actions",
      type: "selection",
      translator: (key: string) => translateNbaType(key as any),
      stringValues: enabledNba,
    },
    {
      id: "BOKA_SERVICES",
      title: "label.boka_services",
      type: "selection",
      translator: (key: string) => translateServices(key as any),
      stringValues: bokaServices ?? [],
    },
    isSalesForceEnabled
      ? {
          id: "INSTALLATION_QUOTATION",
          title: "label.installation_quotation",
          type: "selection",
          stringValues: [
            "have_installation_quotation",
            "no_installation_quotation",
          ],
          translator: (key: string) => t(`label.${key}`),
          additionalControl: { showSearch: false, selectionMode: "SINGLE" },
        }
      : undefined,
    isSalesForceEnabled
      ? {
          id: "INSTALLATION_QUOTATION_STATUS",
          title: "label.installation_quotation_status",
          type: "selection",
          translator: (key: string) => {
            return translateInstallationQuotationStatus(key as any) ?? key;
          },
          stringValues: installationQuotationStatusList,
          additionalControl: { showSearch: false },
        }
      : undefined,

    isSalesForceEnabled
      ? {
          id: "WORKORDER_STATUS",
          title: "label.workorder_status",
          type: "selection",
          stringValues: workOrderStatusList,
          additionalControl: { showSearch: false },
        }
      : undefined,
    isSalesForceEnabled
      ? {
          id: "WORKORDER_TYPE",
          title: "label.workorder_type",
          type: "selection",
          stringValues: workOrderTypeList,
          additionalControl: { showSearch: false },
        }
      : undefined,
    isKICEnabled
      ? {
          id: "INSTALLATION_TYPE",
          title: "label.installation_type",
          type: "selection",
          translator: (key: string) => {
            return translateInstallationType(key as any);
          },
          stringValues: ["KITCHEN"],
          additionalControl: { showSearch: false },
        }
      : undefined,
    isKICEnabled
      ? {
          id: "INSTALLATION_DATE",
          title: "label.installation_date",
          type: "date_range",
        }
      : undefined,
    {
      id: "EXPECTED_PURCHASE_DATE",
      title: "label.expected_purchase_date",
      type: "date_range",
    },
    {
      id: "BUDGET",
      title: "label.budget",
      type: "value_range",
    },
  ].filter((value) => value !== undefined) as WidgetSettingsDefinition[];
